/**
 * Hooks para telemetría
 *
 * Uso:
 *   import { useLogger, useAnalytics, useErrorTracking } from '@tankef/telemetry';
 *
 *   function MyComponent() {
 *     const logger = useLogger();
 *     const { track } = useAnalytics();
 *     const { captureError } = useErrorTracking();
 *
 *     // Logging
 *     logger.info('User opened modal', { modal: 'transfer' });
 *
 *     // Analytics
 *     track('TRANSFER_STARTED', { source: 'dashboard' });
 *
 *     // Error tracking
 *     try {
 *       await doSomething();
 *     } catch (error) {
 *       captureError(error, { action: 'doSomething' });
 *     }
 *   }
 */
import { useContext, useCallback, useEffect } from 'react';
import { TelemetryContext } from './provider';
import type { TelemetryProvider, LogContext, AnalyticsEventName } from './index';

/**
 * Hook base - acceso al provider completo
 */
export function useTelemetry(): TelemetryProvider {
  const context = useContext(TelemetryContext);

  if (!context) {
    throw new Error(
      'useTelemetry must be used within TelemetryProvider. ' +
        'Make sure to wrap your app with <TelemetryProviderComponent>.'
    );
  }

  return context;
}

/**
 * Hook para logging estructurado
 */
export function useLogger() {
  const telemetry = useTelemetry();

  return {
    debug: useCallback(
      (message: string, context?: LogContext) =>
        telemetry.log('debug', message, context),
      [telemetry]
    ),
    info: useCallback(
      (message: string, context?: LogContext) =>
        telemetry.log('info', message, context),
      [telemetry]
    ),
    warn: useCallback(
      (message: string, context?: LogContext) =>
        telemetry.log('warn', message, context),
      [telemetry]
    ),
    error: useCallback(
      (message: string, context?: LogContext) =>
        telemetry.log('error', message, context),
      [telemetry]
    ),
  };
}

/**
 * Hook para analytics/product events
 */
export function useAnalytics() {
  const telemetry = useTelemetry();

  return {
    track: useCallback(
      (name: AnalyticsEventName, properties?: Record<string, unknown>) =>
        telemetry.track({ name, properties }),
      [telemetry]
    ),
    identify: useCallback(
      (userId: string, traits?: Record<string, unknown>) =>
        telemetry.identify(userId, traits),
      [telemetry]
    ),
  };
}

/**
 * Hook para error tracking
 */
export function useErrorTracking() {
  const telemetry = useTelemetry();

  return {
    captureError: useCallback(
      (error: Error, context?: LogContext) =>
        telemetry.captureError(error, context),
      [telemetry]
    ),
  };
}

/**
 * Hook para medir performance
 *
 * Uso:
 *   const { measure } = usePerformance();
 *
 *   const result = await measure('api_call', () => fetchData());
 */
export function usePerformance() {
  const telemetry = useTelemetry();

  return {
    measure: useCallback(
      async <T>(
        name: string,
        fn: () => Promise<T>,
        attributes?: Record<string, unknown>
      ): Promise<T> => {
        const span = telemetry.startSpan(name, attributes);
        try {
          return await fn();
        } finally {
          telemetry.endSpan(span);
        }
      },
      [telemetry]
    ),
  };
}

/**
 * Hook para tracking automático de page views
 *
 * Uso:
 *   function DashboardPage() {
 *     usePageTracking('dashboard');
 *     return <Dashboard />;
 *   }
 */
export function usePageTracking(pageName: string) {
  const { track } = useAnalytics();

  useEffect(() => {
    track('page_view' as AnalyticsEventName, { page: pageName });
  }, [pageName, track]);
}
